import { useState, useCallback, useRef } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Circle,
  Autocomplete,
} from "@react-google-maps/api";
import { Search, MapPin } from "lucide-react";

const MAX_DISTANCE = 40000000; // 40000km in meters
const MAX_KM_DISTANCE = MAX_DISTANCE / 1000;
const MAX_KM_DISTANCE_FORMATTED = MAX_KM_DISTANCE.toLocaleString("en-US");

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const defaultCenter = {
  lat: 40.7128, // Default to New York
  lng: -74.006,
};

interface LocationSelectorProps {
  onLocationSelected: (location: {
    address: string;
    latitude: number;
    longitude: number;
  }) => void;
  initialLocation?: {
    address: string;
    latitude: number;
    longitude: number;
  };
}

export const LocationSelector = ({
  onLocationSelected,
  initialLocation,
}: LocationSelectorProps) => {
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

  const [userLocation, setUserLocation] =
    useState<google.maps.LatLngLiteral | null>(null);
  const [selectedLocation, setSelectedLocation] =
    useState<google.maps.LatLngLiteral | null>(
      initialLocation
        ? { lat: initialLocation.latitude, lng: initialLocation.longitude }
        : null
    );
  const [searchQuery, setSearchQuery] = useState(
    initialLocation?.address || ""
  );
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    // Get user's current location
    if (navigator.geolocation) {
      setIsLoadingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(userPos);
          map.panTo(userPos);
          setIsLoadingLocation(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          setIsLoadingLocation(false);
        }
      );
    }
  }, []);

  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng || !userLocation) return;

      const clickedPos = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };

      // Check if within allowed radius
      if (isWithinAllowedRadius(clickedPos.lat, clickedPos.lng)) {
        setSelectedLocation(clickedPos);

        // Reverse geocode to get address
        getAddressFromCoordinates(clickedPos.lat, clickedPos.lng);
      } else {
        alert(
          `Please select a location within ${MAX_KM_DISTANCE_FORMATTED}km of your current position`
        );
      }
    },
    [userLocation]
  );

  const isWithinAllowedRadius = (lat: number, lng: number) => {
    // Default to true since it's for admins
    return true;

    console.log("🚀 ~ isWithinAllowedRadius ~ lat:", lat);
    console.log("🚀 ~ isWithinAllowedRadius ~ lng:", lng);

    if (!userLocation) return false;

    // const R = 6371e3; // Earth radius in meters
    // const φ1 = (userLocation.lat * Math.PI) / 180;
    // const φ2 = (lat * Math.PI) / 180;
    // const Δφ = ((lat - userLocation.lat) * Math.PI) / 180;
    // const Δλ = ((lng - userLocation.lng) * Math.PI) / 180;

    // const a =
    //   Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    //   Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    // const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    // const distance = R * c;

    // return distance <= MAX_DISTANCE;
  };

  const getAddressFromCoordinates = async (lat: number, lng: number) => {
    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results && response.results.length > 0) {
        const address = response.results[0].formatted_address;
        setSearchQuery(address);

        onLocationSelected({
          address,
          latitude: lat,
          longitude: lng,
        });
      }
    } catch (error) {
      console.error("Error getting address:", error);
    }
  };

  const onAutocompleteLoad = (
    autocomplete: google.maps.places.Autocomplete
  ) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();

      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        if (isWithinAllowedRadius(lat, lng)) {
          const newLocation = { lat, lng };
          setSelectedLocation(newLocation);
          mapRef.current?.panTo(newLocation);
          setSearchQuery(place.formatted_address || place.name || "");

          onLocationSelected({
            address: place.formatted_address || place.name || "",
            latitude: lat,
            longitude: lng,
          });
        } else {
          alert(
            `Please select a location within ${MAX_KM_DISTANCE_FORMATTED}km of your current position`
          );
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={["places"]}>
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-80 z-10" />
          <Autocomplete
            onLoad={onAutocompleteLoad}
            onPlaceChanged={onPlaceChanged}
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for a location..."
              className="w-full pl-10 pr-4 py-2 border border-dark-30 rounded-[5px] bg-dark-20 text-dark-text placeholder-dark-80 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            />
          </Autocomplete>
        </div>

        {/* Map */}
        <div className="relative border border-dark-30 rounded-[5px] overflow-hidden">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={13}
            center={selectedLocation || userLocation || defaultCenter}
            onLoad={onMapLoad}
            onClick={onMapClick}
            options={{
              styles: [
                {
                  featureType: "all",
                  elementType: "geometry",
                  stylers: [{ color: "#242f3e" }],
                },
                {
                  featureType: "all",
                  elementType: "labels.text.stroke",
                  stylers: [{ color: "#242f3e" }],
                },
                {
                  featureType: "all",
                  elementType: "labels.text.fill",
                  stylers: [{ color: "#746855" }],
                },
              ],
            }}
          >
            {/* User Location Marker */}
            {userLocation && (
              <Marker
                position={userLocation}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: "#4B91F7",
                  fillOpacity: 1,
                  strokeColor: "#FFFFFF",
                  strokeWeight: 2,
                }}
              />
            )}

            {/* Selected Location Marker */}
            {selectedLocation && (
              <Marker
                position={selectedLocation}
                icon={{
                  url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                }}
              />
            )}

            {/* Radius Circle */}
            {userLocation && (
              <Circle
                center={userLocation}
                radius={MAX_DISTANCE}
                options={{
                  fillColor: "#4285F4",
                  fillOpacity: 0.1,
                  strokeColor: "#4285F4",
                  strokeOpacity: 0.5,
                  strokeWeight: 2,
                }}
              />
            )}
          </GoogleMap>

          {/* Loading overlay */}
          {isLoadingLocation && (
            <div className="absolute inset-0 bg-dark-5/80 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-dark-text text-sm">
                  Getting your location...
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Info text */}
        <div className="flex items-start gap-2 p-3 bg-dark-20 border border-dark-30 rounded-[5px]">
          <MapPin className="w-4 h-4 text-dark-100 flex-shrink-0 mt-0.5" />
          <p className="text-dark-100 text-sm">
            Select a location within {MAX_KM_DISTANCE_FORMATTED}km of your
            current position. Click on the map or search for a specific place.
          </p>
        </div>
      </LoadScript>
    </div>
  );
};
